import React, { useState, useRef, useEffect } from 'react';
import { FaEllipsisV, FaChevronDown, FaChevronUp, FaTrophy } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';

const renderFormattedText = (text) => {
  if (!text) return null;

  const lines = text.split('\n');
  return lines.map((line, lineIndex) => {
    const lineKey = `line-${lineIndex}`;
    let content;

    if (line.startsWith('• ')) {
      content = (
        <div key={lineKey} className="flex">
          <span className="w-3 flex-shrink-0">•</span>
          <span>{line.substring(2)}</span>
        </div>
      );
    } else if (line.startsWith('    ')) {
      content = (
        <div key={lineKey} className="pl-3">
          {line.trim()}
        </div>
      );
    } else {
      content = (
        <div key={lineKey}>
          {line}
        </div>
      );
    }

    return content;
  });
};

const TopicItem = ({ topic, onRemove, onEdit, onMove, onToggleMastery }) => {
  const navigate = useNavigate();
  const [expanded, setExpanded] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);

  const toggleExpansion = () => setExpanded(!expanded);
  const toggleMenu = () => setMenuOpen(!menuOpen);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  if (!topic) {
    return null;
  }

  // Find the highlighted concept and other concepts
  const highlightedConcept = topic.concepts?.find(concept => concept.is_highlighted);
  const otherConcepts = topic.concepts?.filter(concept => !concept.is_highlighted) || [];

  const handleTopicClick = (e) => {
    // Prevent redirection if clicking on menu or expand/collapse button
    if (e.target.closest('.topic-menu') || e.target.closest('.expand-button')) {
      return;
    }
    navigate(`/topic-detail/${topic.id}`, { 
      state: { 
        topic, 
        basket: { 
          id: topic.basket_id, 
          name: topic.basket?.name || 'Unknown Basket'
        },
        fromBasket: true,
        topicChanged: false
      } 
    });
  };

  const tagColor = topic?.tag?.color || topic?.tags?.color || '#981F2B';

  return (
    <div 
      className={`
        mb-3 rounded-xl border border-neutral-900 p-3 sm:p-4 
        cursor-pointer hover:shadow-lg transition-shadow duration-300 relative group
        ${topic.is_master 
          ? 'bg-butter-50 border-butter-500' 
          : 'bg-white'
        }
      `}
      onClick={handleTopicClick}
    >
      {/* Relation description tooltip */}
      {topic.relation_description && (
        <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-200 absolute -top-12 left-1/2 transform -translate-x-1/2 bg-neutral-900 text-white px-3 py-1.5 rounded-lg text-sm max-w-xs text-center z-20 pointer-events-none">
          {topic.relation_description}
          <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 translate-y-1/2 border-8 border-transparent border-t-neutral-900" />
        </div>
      )}

      {/* Topic header with title and mastery toggle */}
      <div className="flex justify-between items-start mb-2">
        <h3 className="text-base sm:text-lg font-semibold text-neutral-900 line-clamp-2 pr-8 flex-1">
          {topic.name}
        </h3>
        <div className="relative group/trophy">
          <button
            onClick={(e) => {
              e.stopPropagation();
              onToggleMastery(topic.id, !topic.is_master);
            }}
            className={`ml-2 p-1.5 rounded-full transition-colors duration-200 ${
              topic.is_master 
                ? 'bg-butter-500 text-butter-900' 
                : 'bg-neutral-100 text-neutral-400 hover:bg-neutral-200'
            }`}
            aria-label={topic.is_master ? "Remove mastery" : "Mark as mastered"}
          >
            <FaTrophy className="w-3.5 h-3.5" />
          </button>
          {/* Tooltip */}
          <div className="opacity-0 group-hover/trophy:opacity-100 transition-opacity duration-200 absolute right-0 top-full mt-1 bg-neutral-900 text-white px-2 py-1 rounded text-xs whitespace-nowrap z-20">
            {topic.is_master ? "Remove mastery" : "Mark as mastered"}
          </div>
        </div>
      </div>

      {/* Tag */}
      {(topic.tag || topic.tags) && (
        <div className="mb-3">
          <span 
            className="inline-block px-2 sm:px-3 py-0.5 rounded-full text-xs border border-neutral-900 max-w-[120px] whitespace-nowrap overflow-hidden text-ellipsis"
            style={{ 
              backgroundColor: `${tagColor}CC`,
              color: getContrastColor(tagColor)
            }}
          >
            {(topic.tag?.name || topic.tags?.name || 'No Tag')}
          </span>
        </div>
      )}
      
      <div className="mt-2 mb-8">
        {highlightedConcept && (
          <div className="relative pl-3 py-1 mb-2">
            <div className="absolute left-0 top-0 bottom-0 w-0.5 bg-cranberry-400"></div>
            <div className="text-blue-700 text-[13px] sm:text-sm line-clamp-3 sm:line-clamp-none font-normal">
              {renderFormattedText(highlightedConcept.text)}
            </div>
          </div>
        )}

        {otherConcepts.length > 0 && expanded && (
          <ul className="space-y-1 sm:space-y-2 mt-2">
            {otherConcepts.map((concept, index) => (
              <li key={index} className="text-blue-700 text-[13px] sm:text-sm flex font-normal">
                <span className="w-3 flex-shrink-0">•</span>
                <span className="line-clamp-2 sm:line-clamp-none">
                  {renderFormattedText(concept.text)}
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>

      {otherConcepts.length > 0 && (
        <div className="absolute bottom-2 left-1/2 transform -translate-x-1/2 expand-button">
          <button
            onClick={(e) => {
              e.stopPropagation();
              toggleExpansion();
            }}
            className="text-neutral-400 hover:text-neutral-600 focus:outline-none"
            aria-label={expanded ? "Collapse concepts" : "Expand concepts"}
          >
            {expanded ? <FaChevronUp size={12} /> : <FaChevronDown size={12} />}
          </button>
        </div>
      )}

      <div className="absolute bottom-2 right-2 topic-menu" ref={menuRef}>
        <button
          onClick={(e) => {
            e.stopPropagation();
            toggleMenu();
          }}
          className="text-neutral-400 hover:text-neutral-600 focus:outline-none"
          aria-label="Topic options"
        >
          <FaEllipsisV size={12} />
        </button>
        {menuOpen && (
          <div className="absolute right-0 bottom-full mb-2 w-48 bg-white rounded-xl border border-neutral-900 shadow-lg z-10 text-sm overflow-hidden">
            <button 
              onClick={(e) => { e.stopPropagation(); onEdit(topic); }} 
              className="block w-full text-left px-3 py-1.5 hover:bg-neutral-50 text-neutral-900"
            >
              Edit
            </button>
            <button 
              onClick={(e) => { e.stopPropagation(); onMove(topic); }} 
              className="block w-full text-left px-3 py-1.5 hover:bg-neutral-50 text-neutral-900"
            >
              Move to another basket
            </button>
            <button 
              onClick={(e) => { 
                e.stopPropagation(); 
                onEdit({ ...topic, showRelationTab: true }); 
              }} 
              className="block w-full text-left px-3 py-1.5 hover:bg-neutral-50 text-neutral-900 flex items-center justify-between"
            >
              <span>{topic.relation_description ? 'Edit Relationship' : 'Add Relationship'}</span>
              {topic.relation_description && (
                <span className="text-xs text-neutral-500 truncate ml-2 max-w-[100px]">
                  {topic.relation_description}
                </span>
              )}
            </button>
            <button 
              onClick={(e) => { e.stopPropagation(); onRemove(topic.id); }} 
              className="block w-full text-left px-3 py-1.5 hover:bg-neutral-50 text-cranberry-700"
            >
              Remove from basket
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

// Helper function to determine text color based on background color
function getContrastColor(hexColor) {
  if (!hexColor || typeof hexColor !== 'string') {
    return 'black'; // Default to black text if no valid color is provided
  }

  try {
    // Convert hex to RGB
    const r = parseInt(hexColor.slice(1, 3), 16);
    const g = parseInt(hexColor.slice(3, 5), 16);
    const b = parseInt(hexColor.slice(5, 7), 16);
    
    // Calculate luminance
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    
    // Return black for light colors, white for dark colors
    return luminance > 0.5 ? 'black' : 'white';
  } catch (error) {
    console.warn('Invalid color format:', hexColor);
    return 'black'; // Default to black text if color parsing fails
  }
}

export default TopicItem;
